import {
  Box,
  Button,
  Flex,
  Heading,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorMode,
} from "@chakra-ui/react";
import type { ReactNode } from "react";
import { CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Tooltip } from "chart.js";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../../common/formatting/formatNumber";
import { useGetPriceDevelopmentQuery, type PriceDevelopmentDto } from "../../../autogen/bff-api";
import { supplierDevelopments, type PriceChange } from "./priceDevelopment";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const SERIES_COLORS = [
  "smChartSeries1",
  "smChartSeries2",
  "smChartSeries3",
  "smChartSeries4",
  "smChartSeries5",
  "smChartSeries6",
];

export const TotalPriceDevelopmentSection = ({ eventId }: { eventId: string }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useGetPriceDevelopmentQuery({ eventId });
  if (isLoading) {
    return (
      <SectionFrame>
        <Skeleton h="200px" rounded="md" />
      </SectionFrame>
    );
  }
  if (isError || data == null) {
    return (
      <SectionFrame>
        <Flex align="center" gap="3" wrap="wrap">
          <Text color="smError">{t("Could not load the total price per round.")}</Text>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            {t("Try again")}
          </Button>
        </Flex>
      </SectionFrame>
    );
  }
  if (data.stages.length < 2 || data.suppliers.length === 0) {
    return (
      <SectionFrame>
        <Text fontSize="sm" color="smMuted">
          {t("Total prices show here once a round has received bids.")}
        </Text>
      </SectionFrame>
    );
  }
  return <TotalPriceDevelopment development={data} />;
};

const SectionFrame = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  return (
    <Box pt="6">
      <Heading size="sm" pb="3">
        {t("Total price per round")}
      </Heading>
      {children}
    </Box>
  );
};

export const TotalPriceDevelopment = ({ development }: { development: PriceDevelopmentDto }) => {
  const { t } = useTranslation();
  const suppliers = supplierDevelopments(development);
  const stageLabel = (roundNumber: number | null | undefined) =>
    roundNumber == null ? t("Original bid") : `${t("Round")} ${roundNumber}`;
  const labels = development.stages.map((stage) => stageLabel(stage.roundNumber));

  return (
    <Box pt="6">
      <Heading size="sm" pb="1">
        {t("Total price per round")}
      </Heading>
      <Text fontSize="sm" color="smMuted" pb="3">
        {t("Each supplier's total price, as used in the evaluation, in the original bid and in each round.")}
      </Text>
      <TableContainer border="1px solid" borderColor="smBorder" rounded="md">
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th>{t("Supplier")}</Th>
              {labels.map((label) => (
                <Th key={label} isNumeric>
                  {label}
                </Th>
              ))}
              <Th isNumeric>{t("Total change")}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {suppliers.map((supplier) => (
              <Tr key={supplier.supplierId}>
                <Td>{supplier.supplierName}</Td>
                {supplier.stages.map((stage, index) => (
                  <Td key={labels[index]} isNumeric verticalAlign="top">
                    {!stage.participated ? (
                      <Text color="smMuted">{index === 0 ? t("No original bid") : t("Not in this round")}</Text>
                    ) : stage.price == null ? (
                      <Text color="smMuted">{t("No price given")}</Text>
                    ) : (
                      <>
                        <Text fontFamily="mono">{formatNumber(stage.price)}</Text>
                        {stage.changeFromPrevious && <Change change={stage.changeFromPrevious} />}
                      </>
                    )}
                  </Td>
                ))}
                <Td isNumeric verticalAlign="top">
                  {supplier.totalChange ? (
                    <Change change={supplier.totalChange} />
                  ) : (
                    <Text color="smMuted">{supplier.inNoRound ? t("Not in any round") : t("n/a")}</Text>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      <PriceChart labels={labels} development={development} />
    </Box>
  );
};

const Change = ({ change }: { change: PriceChange }) => {
  const { i18n } = useTranslation();
  const percent = new Intl.NumberFormat(i18n.language === "no" ? "nb-NO" : "en-GB", {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Math.abs(change.percent) / 100);
  const color = change.amount > 0 ? "smError" : change.amount < 0 ? "smSuccess" : "smMuted";
  const sign = change.amount > 0 ? "+" : change.amount < 0 ? "\u2212" : "";
  return (
    <Text fontSize="xs" color={color} fontFamily="mono">
      {`${sign}${percent} (${sign}${formatNumber(Math.abs(change.amount))})`}
    </Text>
  );
};

const PriceChart = ({ labels, development }: { labels: string[]; development: PriceDevelopmentDto }) => {
  const { t } = useTranslation();
  const { colorMode } = useColorMode();
  const resolveToken = (token: string) => {
    const value = getComputedStyle(document.documentElement).getPropertyValue(`--chakra-colors-${token}`).trim();
    return value || token;
  };
  return (
    <Box pt="4" h="280px" role="img" aria-label={t("Total price per round") ?? undefined}>
      <Line
        key={colorMode}
        data={{
          labels,
          datasets: development.suppliers.map((supplier, index) => {
            const color = resolveToken(SERIES_COLORS[index % SERIES_COLORS.length]);
            return {
              label: supplier.supplierName,
              data: supplier.prices.map((price) => (price == null ? null : Number(price))),
              borderColor: color,
              backgroundColor: color,
              spanGaps: false,
            };
          }),
        }}
        options={{
          maintainAspectRatio: false,
          plugins: {
            legend: { position: "bottom" },
            tooltip: {
              callbacks: {
                label: (item) => `${item.dataset.label}: ${formatNumber(item.parsed.y)} ${development.currency}`,
              },
            },
          },
          scales: { y: { ticks: { callback: (value) => formatNumber(Number(value)) } } },
        }}
      />
    </Box>
  );
};
